/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { appPalette } from '@sidekick/design-tokens';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import { trpc } from '../lib/trpc.js';

const p = appPalette;

const monoLabel: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: p.muted,
};

const inputStyle: React.CSSProperties = {
  padding: '10px 12px',
  background: p.bg,
  color: p.fg,
  border: `1px solid ${p.line}`,
  fontFamily: 'var(--font-serif)',
  fontSize: 15,
  outline: 'none',
};

/**
 * Counsel — the conversational coaching surface.
 *
 * Pick a recent conversation to resume or start a new one; the thread
 * renders the turns and a message box drives `conversations.send` (Coach
 * mode). Multi-turn — the coach reads the whole thread each turn.
 */
export function CounselPage() {
  const [selected, setSelected] = useState<{ id: string; initialMessage: string | null } | null>(
    null,
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 22px',
          borderBottom: `1px solid ${p.line}`,
        }}
      >
        <span style={monoLabel}>Counsel</span>
        <span style={{ ...monoLabel, color: p.fg }}>Talk it through with your Second</span>
      </div>

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '40px 22px' }}>
        <div style={{ width: '100%', maxWidth: 620, display: 'flex', flexDirection: 'column' }}>
          {selected ? (
            <Thread
              conversationId={selected.id}
              initialMessage={selected.initialMessage}
              onBack={() => setSelected(null)}
            />
          ) : (
            <Lobby onOpen={(id, initialMessage) => setSelected({ id, initialMessage })} />
          )}
        </div>
      </div>
    </div>
  );
}

function Lobby({
  onOpen,
}: {
  onOpen: (conversationId: string, initialMessage: string | null) => void;
}) {
  const utils = trpc.useUtils();
  const list = trpc.conversations.list.useQuery();
  const [message, setMessage] = useState('');
  const [mode, setMode] = useState<'coach' | 'strategy'>('coach');
  const start = trpc.conversations.start.useMutation({
    onSuccess: async ({ conversationId }) => {
      await utils.conversations.list.invalidate();
      // Hand the typed message to the thread, which sends it as the
      // opening turn — so "Begin" both starts and asks in one step.
      onOpen(conversationId, message.trim());
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const text = message.trim();
    if (text === '') return;
    // Derive a short topic from the opening message (column max 200).
    start.mutate({ mode, topic: text.slice(0, 120) });
  };

  const conversations = list.data ?? [];

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={monoLabel}>What's on your mind?</span>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Ask your Second anything — a decision, a tension, a plan to pressure-test."
          aria-label="What's on your mind?"
          rows={3}
          style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as 'coach' | 'strategy')}
            aria-label="Conversation mode"
            style={{ ...inputStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}
          >
            <option value="coach">Coach</option>
            <option value="strategy">Strategy</option>
          </select>
          <button
            type="submit"
            disabled={start.isPending || message.trim() === ''}
            style={{
              padding: '10px 18px',
              background: p.fg,
              color: p.bg,
              border: 'none',
              fontSize: 13,
              cursor: start.isPending || message.trim() === '' ? 'not-allowed' : 'pointer',
              opacity: start.isPending || message.trim() === '' ? 0.6 : 1,
            }}
          >
            {start.isPending ? 'Opening…' : 'Begin →'}
          </button>
        </div>
        {start.error ? (
          <span style={{ ...monoLabel, color: p.accent }}>{start.error.message}</span>
        ) : null}
      </form>

      {conversations.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <span style={{ ...monoLabel, marginBottom: 6 }}>Recent</span>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {conversations.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => onOpen(c.id, null)}
                  style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: 12,
                    padding: '12px 0',
                    borderTop: `1px solid ${p.line}`,
                    background: 'transparent',
                    border: 'none',
                    borderTopWidth: 1,
                    borderTopStyle: 'solid',
                    borderTopColor: p.line,
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit',
                    color: 'inherit',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-serif)',
                      fontFeatureSettings: 'var(--serif-feat)',
                      fontSize: 15,
                      color: p.fg,
                    }}
                  >
                    {c.topic}
                  </span>
                  <span style={{ ...monoLabel }}>{c.mode}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function Thread({
  conversationId,
  initialMessage,
  onBack,
}: {
  conversationId: string;
  initialMessage: string | null;
  onBack: () => void;
}) {
  const utils = trpc.useUtils();
  const loaded = trpc.conversations.load.useQuery({ conversationId });
  const [draft, setDraft] = useState('');
  const send = trpc.conversations.send.useMutation({
    onSuccess: async () => {
      await utils.conversations.load.invalidate({ conversationId });
      setDraft('');
    },
  });

  // Auto-send the opening message handed over from the lobby, exactly
  // once, so starting a conversation also asks the first question.
  const autoSent = useRef(false);
  useEffect(() => {
    const opener = initialMessage?.trim();
    if (opener && !autoSent.current) {
      autoSent.current = true;
      send.mutate({ conversationId, message: opener });
    }
  }, [conversationId, initialMessage, send]);

  const onSend = (e: FormEvent) => {
    e.preventDefault();
    if (draft.trim() === '' || send.isPending) return;
    send.mutate({ conversationId, message: draft.trim() });
  };

  const turns = loaded.data?.turns ?? [];

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <span style={{ ...monoLabel }}>{loaded.data?.conversation.topic ?? '…'}</span>
        <button
          type="button"
          onClick={onBack}
          style={{
            ...monoLabel,
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          ← all
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {turns.map((turn) => (
          <Turn key={turn.id} role={turn.role} content={turn.content} />
        ))}
        {send.isPending ? <span style={{ ...monoLabel }}>your Second is thinking…</span> : null}
      </div>

      <form onSubmit={onSend} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Say more…"
          aria-label="Your message"
          rows={2}
          style={{ ...inputStyle, flex: 1, resize: 'vertical', minHeight: 56 }}
        />
        <button
          type="submit"
          disabled={send.isPending || draft.trim() === ''}
          style={{
            padding: '10px 18px',
            background: p.fg,
            color: p.bg,
            border: 'none',
            fontSize: 13,
            cursor: send.isPending || draft.trim() === '' ? 'not-allowed' : 'pointer',
            opacity: send.isPending || draft.trim() === '' ? 0.6 : 1,
          }}
        >
          {send.isPending ? 'Sending…' : 'Send →'}
        </button>
      </form>
      {send.error ? (
        <span style={{ ...monoLabel, color: p.accent }}>{send.error.message}</span>
      ) : null}
    </section>
  );
}

function Turn({ role, content }: { role: 'user' | 'assistant'; content: string }) {
  const isUser = role === 'user';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        alignItems: isUser ? 'flex-end' : 'flex-start',
      }}
    >
      <span style={{ ...monoLabel }}>{isUser ? 'You' : 'Your Second'}</span>
      <div
        style={{
          maxWidth: '85%',
          padding: '10px 14px',
          background: isUser ? p.bgAlt : 'transparent',
          border: isUser ? 'none' : `1px solid ${p.line}`,
          fontFamily: 'var(--font-serif)',
          fontFeatureSettings: 'var(--serif-feat)',
          fontSize: 15,
          lineHeight: 1.5,
          color: p.fg,
        }}
      >
        {isUser ? content : <ReactMarkdown>{content}</ReactMarkdown>}
      </div>
    </div>
  );
}
